import axios from 'axios';
import {getAuthHeaders, getBaseUrl} from '../helpers/rulesApiHelpers';

export const ADMIN_TENANTS_FETCH_REQUEST = 'ADMIN_TENANTS_FETCH_REQUEST';
export const ADMIN_TENANTS_FETCH_SUCCESS = 'ADMIN_TENANTS_FETCH_SUCCESS';
export const ADMIN_TENANTS_FETCH_FAILURE = 'ADMIN_TENANTS_FETCH_FAILURE';
export const ADMIN_TENANTS_TEST_TENANT_SAVED =
  'ADMIN_TENANTS_TEST_TENANT_SAVED';

const DEFAULT_LIMIT = 20;

let latestRequestId = 0;

export function buildError(kind, status, message) {
  return Object.assign(new Error(message), {kind, status});
}

export function firstMessage(body, fallback) {
  const messages =
    body && Array.isArray(body.error_messages) ? body.error_messages : [];
  return messages[0] || fallback;
}

export function classifyFailure(error) {
  if (error && error.kind) return error;
  const response = error && error.response;
  if (!response) {
    return buildError(
      'network',
      null,
      (error && error.message) || 'Network error'
    );
  }
  const {status, data} = response;
  if (status === 401)
    return buildError(
      'unauthorized',
      status,
      firstMessage(data, 'Unauthorized')
    );
  if (status === 403)
    return buildError('forbidden', status, firstMessage(data, 'Forbidden'));
  if (data && data.status === false) {
    return buildError('rejected', status, firstMessage(data, error.message));
  }
  return buildError(
    'server',
    status,
    error.message || `Request failed with status ${status}`
  );
}

function pickCount(counts, status, searchActive) {
  if (searchActive) return counts.search;
  return counts[status] !== undefined ? counts[status] : counts.all;
}

export function fetchTenants({
  skip,
  limit,
  sortInfo,
  searchQuery,
  status,
  allTenantSorting
} = {}) {
  return async dispatch => {
    latestRequestId += 1;
    const requestId = latestRequestId;
    dispatch({type: ADMIN_TENANTS_FETCH_REQUEST, requestId});
    try {
      const base = await getBaseUrl();
      const headers = await getAuthHeaders();
      const search = (searchQuery || '').trim();
      const statusFilter = status || 'active';
      const params = {
        search,
        sort: (sortInfo && sortInfo.name) || '',
        order: sortInfo && sortInfo.dir === 1 ? 'ASC' : 'DESC',
        limit: limit || DEFAULT_LIMIT,
        offset: skip || 0,
        status: statusFilter
      };
      // GROOV-5182 slices a search page twice when pages_sort is set.
      if (allTenantSorting && search === '') params.pages_sort = 'true';

      const response = await axios.get(`${base}/tenants.json`, {
        headers,
        params
      });
      const body = response.data;
      if (!body || body.status === false) {
        throw buildError(
          'rejected',
          response.status || null,
          firstMessage(body, 'The server rejected the request')
        );
      }
      if (!Array.isArray(body.tenants) || !body.tenants_count) {
        throw buildError(
          'server',
          response.status || null,
          'Unexpected response from the server'
        );
      }

      if (requestId !== latestRequestId) return {stale: true};

      const tenantsCount = body.tenants_count;
      const count = pickCount(tenantsCount, statusFilter, search !== '');
      const hasPills =
        tenantsCount.active !== undefined && tenantsCount.deleted !== undefined;
      const counts = hasPills
        ? {
            active: tenantsCount.active,
            deleted: tenantsCount.deleted,
            all: tenantsCount.all
          }
        : null;
      dispatch({
        type: ADMIN_TENANTS_FETCH_SUCCESS,
        requestId,
        counts,
        lastLoadedAt: Date.now()
      });
      return {data: body.tenants, count};
    } catch (caught) {
      if (requestId !== latestRequestId) return {stale: true};
      const failure = classifyFailure(caught);
      dispatch({
        type: ADMIN_TENANTS_FETCH_FAILURE,
        requestId,
        error: {
          kind: failure.kind,
          status: failure.status,
          message: failure.message
        }
      });
      throw failure;
    }
  };
}

const isEmptyBody = body => body === '' || body === null || body === undefined;

function isSuccessBody(body, statuslessObjectOk) {
  if (typeof body !== 'object' || Array.isArray(body)) return false;
  const hasErrors =
    Array.isArray(body.error_messages) && body.error_messages.length > 0;
  if (statuslessObjectOk && !('status' in body)) return !hasErrors;
  if (!body.status || body.status === 'false') return false;
  return body.status === true || !hasErrors;
}

// update_import_mode answers a bare {} on success (tenants_controller.rb, GROOV-5182).
async function runMutation(request, {statuslessObjectOk = false} = {}) {
  try {
    const base = await getBaseUrl();
    const headers = await getAuthHeaders();
    const response = await request(base, headers);
    const body = response.data;
    if (isEmptyBody(body)) return {ok: true, message: ''};
    if (!isSuccessBody(body, statuslessObjectOk)) {
      return {
        ok: false,
        error: firstMessage(body, 'The server rejected the request')
      };
    }
    const messages = Array.isArray(body.success_messages)
      ? body.success_messages
      : [];
    return {ok: true, message: messages[0] || ''};
  } catch (caught) {
    return {ok: false, error: classifyFailure(caught).message};
  }
}

export function deleteTenant(row) {
  return () =>
    runMutation((base, headers) =>
      axios.post(`${base}/tenants/delete_tenant.json`, [row], {headers})
    );
}

export function duplicateTenant(row, newName) {
  return () =>
    runMutation((base, headers) =>
      axios.post(
        `${base}/tenants/${
          row.id
        }/create_duplicate.json?name=${encodeURIComponent(newName)}`,
        null,
        {headers}
      )
    );
}

export function restoreTenant(row) {
  return () =>
    runMutation((base, headers) =>
      axios.post(
        `${base}/tenants/restore_tenant`,
        {tenant_id: row.id, tenant_name: row.name},
        {headers}
      )
    );
}

export function deleteImportSummary(row) {
  return () =>
    runMutation((base, headers) =>
      axios.get(`${base}/tenants/delete_summary.json?tenant=${row.id}`, {
        headers
      })
    );
}

export function setTestTenant(row, next) {
  return async dispatch => {
    const result = await runMutation(
      (base, headers) =>
        axios.get(`${base}/tenants/update_import_mode.json`, {
          headers,
          params: {
            tenant: row.id,
            test_tenant_toggle: next,
            scheduled_import_toggle: row.scheduled_import_toggle,
            inventory_report_toggle: row.inventory_report_toggle
          }
        }),
      {statuslessObjectOk: true}
    );
    if (result.ok) {
      dispatch({
        type: ADMIN_TENANTS_TEST_TENANT_SAVED,
        tenantId: row.id,
        value: next
      });
    }
    return result;
  };
}

// Not 'tenants': the Angular grid saves its own column keys under that identifier.
export const TENANT_COLUMNS_PREFERENCE = 'gpx_tenants';

export function fetchTenantColumnPrefs() {
  return async () => {
    try {
      const base = await getBaseUrl();
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${base}/settings/get_columns_state.json`,
        {
          headers,
          params: {identifier: TENANT_COLUMNS_PREFERENCE}
        }
      );
      const body = response.data;
      if (!body || typeof body !== 'object' || body.status !== true) {
        const messages =
          body && Array.isArray(body.messages) ? body.messages : [];
        return {
          ok: false,
          error: messages[0] || 'The server rejected the request'
        };
      }
      const theads = body.data && body.data.theads;
      return {ok: true, visible: Array.isArray(theads) ? theads : null};
    } catch (caught) {
      return {ok: false, error: classifyFailure(caught).message};
    }
  };
}

export function saveTenantColumnPrefs(visibleNames) {
  return () =>
    runMutation((base, headers) =>
      axios.post(
        `${base}/settings/save_columns_state.json`,
        {identifier: TENANT_COLUMNS_PREFERENCE, theads: visibleNames},
        {headers}
      )
    );
}
